import { getQueryClient } from '@eventbrite/get-create-query-client';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { useContext, useMemo } from 'react';
import { CompletionType } from '../../api/searchAutocomplete';
import {
    ErrorDisplayProvider,
    LocationInputProvider,
    LocationProvider,
    SearchDropdownContext,
    SearchDropdownProvider,
    SearchInputProvider,
} from '../../context';
import { CamelCaseLocation } from '../../types';
import SearchBar from './SearchBar/SearchBar';

export interface SearchBarContainerProps {
    /**
     * Location object to be used for the search bar
     */
    location?: CamelCaseLocation;
    id?: string;
    onSelect?: (value: string) => void;
    onChange?: (value: string) => void;
    args?: any[];
    initialQuery?: string;
    completionTypes?: CompletionType[];
    usesLanguageSubdirectory?: boolean;
    locale?: string;
    onAutocompleteCallback?: () => void;
}

const client = getQueryClient();

export const SearchBarProviders: React.FunctionComponent<
    SearchBarContainerProps
> = ({
    location,
    initialQuery,
    children,
    usesLanguageSubdirectory,
    locale,
    completionTypes,
    onAutocompleteCallback,
}) => {
    const { initialized } = useContext(SearchDropdownContext);
    const wrappedChildren = useMemo(
        () =>
            initialized ? (
                children
            ) : (
                <SearchDropdownProvider>{children}</SearchDropdownProvider>
            ),
        [initialized, children],
    );

    return (
        <QueryClientProvider client={client}>
            <ErrorDisplayProvider>
                <LocationProvider location={location}>
                    <LocationInputProvider>
                        <SearchInputProvider
                            initialQuery={initialQuery}
                            usesLanguageSubdirectory={usesLanguageSubdirectory}
                            locale={locale}
                            completionTypes={completionTypes}
                            onAutocompleteCallback={onAutocompleteCallback}
                        >
                            {wrappedChildren}
                        </SearchInputProvider>
                    </LocationInputProvider>
                </LocationProvider>
            </ErrorDisplayProvider>
        </QueryClientProvider>
    );
};

export const SearchBarContainerWithProviders: React.FunctionComponent<
    SearchBarContainerProps & { isAISearchEnabled?: boolean }
> = ({
    location,
    initialQuery,
    isAISearchEnabled,
    completionTypes,
    onAutocompleteCallback,
}) => (
    <SearchBarProviders
        location={location}
        initialQuery={initialQuery}
        completionTypes={completionTypes}
        onAutocompleteCallback={onAutocompleteCallback}
    >
        <SearchBar isAISearchEnabled={isAISearchEnabled || false} />
    </SearchBarProviders>
);
